import AutoCompleteBrands from '@components/AutoComplete/AutoCompleteBrands';
import { AutoCompleteTalentProfessions } from '@components/AutoComplete/AutoCompleteTalentProfessions';
import { AutocompleteWowEffect } from '@components/AutoComplete/AutocompleteWowEffect';
import InputAutocompleteCity from '@components/InputAutocompleteCity';
import InputDate from '@components/InputDate';
import InputFieldControlled from '@components/InputFieldControlled';
import InputSwitchControl from '@components/InputSwitchControl';
import Popover from '@components/Popover';
import { useOptionsContext } from '@modules/talents/context/options-context';
import { Typography } from '@mui/material';
import { moment } from '@utils/date.utils';
import { useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';

type Props = {
    pro: any;
};

const JobOfferProfileForm = ({ pro }: Props) => {
    const { data: optionsData } = useOptionsContext();
    const options = useMemo(() => optionsData?.options, [optionsData]);
    const { control, setValue, register, getValues } = useFormContext();
    const values = getValues();

    const [enablePublishDate, setEnablePublishDate] = useState(values?.enablePublishDate);

    return (
        <>
            <InputFieldControlled
                name="title"
                control={control}
                label="Titre de l'offre"
                placeholder="Saisissez le titre du poste, la mention H/F sera ajoutée automatiquement"
                endAdornment={
                    <Popover hover>
                        <Typography fontWeight={700}>
                            Titre de l'offre
                        </Typography>
                        <Typography variant="body2">
                            Saisissez le titre du poste au masculin (ex. : Coiffeur expérimenté). La mention H/F est
                            ajoutée automatiquement.
                        </Typography>
                    </Popover>
                }
                required
            />

            <AutoCompleteBrands name="brandId" control={control} label="Marque / Enseigne" proId={pro?._id} required />

            <InputFieldControlled
                name="address"
                control={control}
                label="Adresse"
                disabled={!pro?.isGroup}
                placeholder="Ex: 12 rue de la paix"
                defaultValue={values?.address}
            />

            <InputAutocompleteCity
                name="city"
                label="Ville"
                disabled={!pro?.isGroup}
                placeholder="Ex: Paris"
                required
                onSelectProp={function (city: string, postalCode: string): void {
                    setValue('city', city);
                    setValue('postalCode', postalCode);
                }}
            />

            <input hidden name="postalCode" {...register('postalCode')} />

            <InputSwitchControl
                control={control}
                name="enablePublishDate"
                defaultValue={values?.enablePublishDate}
                title="Date de publication"
                style={{ alignItems: 'center' }}
                onChange={val => {
                    setEnablePublishDate(val);
                    if (!val) {
                        setValue('publishDate', null);
                    }
                }}
                endAdornment={undefined}
                disabled={undefined}
            />

            
            {enablePublishDate && (
                <InputDate
                    placeholder="ex : Jeudi 12 janvier 2023"
                    initialDate={values?.publishDate ? moment(values?.publishDate).toDate() : new Date()}
                    minDate={moment(new Date())}
                    name="publishDate"
                    defaultValue={values?.publishDate}
                    control={control}
                    rules={{
                        requiredIfTrue: val => {
                            const isRequired = enablePublishDate === true;
                            return isRequired ? !!val : true;
                        },
                    }}
                />
            )}

            <AutoCompleteTalentProfessions
                allowAdd
                label="Fonction"
                placeholder="Sélectionnez la fonction recherchée"
                groupedOptions={options?.groupProfessions}
                control={control}
                name="position.professions"
                defaultValue={values?.position?.professions}
                multiple={true}
                rules={{
                    required: { value: true },
                }}
            />

            <InputFieldControlled
                label="Nombre de postes disponibles"
                placeholder="Ex: 2"
                name="position.numberOfPositions"
                control={control}
                disabled={!pro?.isGroup}
                keyboardType="number-pad"
                defaultValue={values?.position?.numberOfPositions?.toString()}
                required
                rules={{
                    minMax: (val: number) => {
                        return (val >= 1 && val <= 100) || 'Le nombre de postes doit être compris entre 1 et 100';
                    },
                }}
            />

            <InputSwitchControl
                control={control}
                name="position.companyName"
                defaultValue={values?.position?.companyName}
                title="Afficher le nom de l'enseigne"
                disabled={undefined}
                endAdornment={
                    <Popover hover>
                        <Typography fontWeight={700}>
                            Afficher le nom de l'enseigne
                        </Typography>
                        <Typography variant="body2">
                            Lorsque vous décidez d'afficher le nom de l'enseigne, l'introduction de votre offre
                            contiendra le texte que vous avez ajouter dans la section "A propos". Si vous souhaitez
                            le modifier, vous pourrez le faire après avoir finaliser votre offre.
                        </Typography>
                        <Typography variant="body2">
                            Si, vous ne souhaitez pas afficher le nom de votre entreprise, Upercut affichera un
                            texte d'introduction automatiquement.
                        </Typography>
                    </Popover>
                }
            />

            <InputSwitchControl
                control={control}
                name="offre_cachee"
                defaultValue={values?.offre_cachee}
                title="Offre confidentielle"
                style={{ alignItems: 'center' }}
                onChange={val => {
                    if (!val) {
                        setValue('offre_cachee', false);
                    }
                }}
                endAdornment={
                    <Popover hover>
                        <Typography fontWeight={700}>
                            Offre confidentielle
                        </Typography>
                        <Typography variant="body2">
                            En activant le bouton "Offre confidentielle" votre offre ne sera pas diffusée auprès
                            d'Upercut et ses partenaires.
                        </Typography>
                        <Typography variant="body2">
                            Cette option vous permet de créer ici, toutes les offres que vous gérez différemment
                            (chasse de profil, offre discrète, utilisation d'autres sources d'acquisition) pour vous
                            permettre de centraliser dans un même endroit tout votre vivier de candidature.
                        </Typography>
                        <Typography variant="body2">
                            Vous pourrez rattacher à cette offre tous les profil que vous souhaitez parmi ceux que
                            vous avez dans votre vivier.
                        </Typography>
                    </Popover>
                }
                disabled={undefined}
            />

            <AutocompleteWowEffect
                label="Proposez un effet Whaou à vos candidats"
                placeholder="ex : on vous offre plein d'avantages"
                groupedOptions={options?.benefits}
                name="contract.benefits"
                control={control}
                allowAdd
            />
        </>
    );
};

export default JobOfferProfileForm;
